import { META } from "./config";
import AccountManagementPage from "./containers/AccountManagementPage";
import AdminAccountManagementPage from "./containers/AdminAccountManagementPage";
import AdminLocationAlertsPage from "./containers/AdminLocationAlertsPage";
import AnalysisCollectionsPage from "./containers/AnalysisCollectionsPage";
import CustomizedReviewsAnalysisPage from "./containers/CustomizedReviewsAnalysisPage";
import DashboardPage from "./containers/DashboardPage";
import DataDownloadPage from "./containers/DataDownloadPage";
import DeleteGoogleLinkPage from "./containers/DeleteGoogleLinkPage";
import { DemoPage } from "./containers/DemoPage";
import ErrorLogsManagementPage from "./containers/ErrorLogsManagementPage";
import ErrorPage from "./containers/ErrorPage";
import OrganizationFeaturesPage from "./containers/EXPERIMENTAL/OrganizationFeaturesPage";
import FacilityDetailsPage from "./containers/FacilityDetailsPage";
import FacilityListPage from "./containers/FacilityListPage";
import FacilityManagementPage from "./containers/FacilityManagementPage";
import FacilityRegistrationPage from "./containers/FacilityRegistrationPage";
import FacilityReportsPage from "./containers/FacilityReportsPage";
import FacilityReviewSummariesPage from "./containers/FacilityReviewSummariesPage";
import { FaqPage } from "./containers/FaqPage";
import FindUserAccountPage from "./containers/FindUserAccountPage";
import FunctionManagementPage from "./containers/FunctionManagementPage";
import InstagramDeletedPage from "./containers/InstagramDeletedPage";
import IntegrateInstagramPage from "./containers/IntegrateInstagramPage";
import InternalLabelsManagementPage from "./containers/InternalLabelsManagementPage";
import InternalToolsPage from "./containers/InternalToolsPage";
import LocalPostsPage from "./containers/LocalPostsPage";
import { LocationActivityPage } from "./containers/LocationActivityPage";
import LocationPreferencesPage from "./containers/LocationPreferencesPage";
import MassPostPage from "./containers/MassPostPage";
import MassSendEmailsPage from "./containers/MassSendEmailsPage";
import MediaCataloguePage from "./containers/MediaCataloguePage";
import MediaPage from "./containers/MediaPage";
import OrganizationManagementPage from "./containers/OrganizationManagementPage";
import OrgToolsPage from "./containers/OrgToolsPage";
import ProjectManagementPage from "./containers/ProjectManagementPage";
import RecMessagesListPage from "./containers/RecMessagesListPage";
import ReviewRequestPrintsPage from "./containers/ReviewRequestPrintsPage";
import ReviewsManagementPage from "./containers/ReviewsManagementPage";
import RootPage from "./containers/RootPage";
import SuggestionsListPage from "./containers/SuggestionsListPage";

/**
 * Generate an object with all necessary fields to render a page.
 * @param {string} path - The page path
 * @param {string} title - THe page title (for SEO)
 * @param {Function} component - The component to be rendered. Containers can also be used
 * @param {string} description - The page description (for SEO) [OPTIONAL]
 * @param {string} keywords - The comma separated page keywords (for SEO) [OPTIONAL]
 * @returns {object}
 */
export const createPage = (
  path: any,
  title: any,
  component: any,
  description?: any,
  keywords?: any,
  isPublic = false
) => ({
  path,
  title: `${title} | ${META.PAGE_TITLE_SUFFIX}`,
  component,
  description: description || META.PAGE_DESCRIPTION,
  keywords: keywords || META.PAGE_KEYWORDS,
  isPublic,
});

const exportRoute = [
  createPage("/", "RootPage", RootPage),
  createPage("/dashboard", "DashboardPage", DashboardPage),
  createPage(
    "/dashboard/organization/:organizationId",
    "DashboardPage",
    DashboardPage
  ),
  createPage(
    "/dashboard/organization/:organizationId/customDashboard/:customDashboardId",
    "DashboardPage",
    DashboardPage
  ),
  createPage("/locations", "FacilityListPage", FacilityListPage),
  createPage(
    "/locations/organization/:organizationId",
    "FacilityListPage",
    FacilityListPage
  ),
  createPage(
    "/location/:locationId",
    "FacilityDetailsPage",
    FacilityDetailsPage
  ),
  createPage(
    "/location/:locationId/localposts",
    "LocalPostsPage",
    LocalPostsPage
  ),
  createPage("/location/:locationId/media", "MediaPage", MediaPage),
  createPage(
    "/location/:locationId/media-catalogue",
    "MediaCataloguePage",
    MediaCataloguePage
  ),
  createPage(
    "/location/:locationId/review-summaries/:reviewSummaryId",
    "FacilityReviewSummariesPage",
    FacilityReviewSummariesPage
  ),
  createPage(
    "/location/:locationId/review-request-prints",
    "ReviewRequestPrintsPage",
    ReviewRequestPrintsPage
  ),
  createPage(
    "/location/:locationId/organization/:organizationId",
    "FacilityDetailsPage",
    FacilityDetailsPage
  ),
  createPage(
    "/location/:locationId/organization/:organizationId/review-summaries/:reviewSummaryId",
    "FacilityReviewSummariesPage",
    FacilityReviewSummariesPage
  ),
  createPage(
    "/location/:locationId/organization/:organizationId/review-request-prints",
    "ReviewRequestPrintsPage",
    ReviewRequestPrintsPage
  ),
  createPage(
    "/location/:locationId/reviews",
    "ReviewsManagementPage",
    ReviewsManagementPage
  ),
  createPage(
    "/location/:locationId/organization/:organizationId/reviews",
    "ReviewsManagementPage",
    ReviewsManagementPage
  ),
  createPage("/recommendations", "SuggestionsListPage", SuggestionsListPage),
  createPage(
    "/recommendations/organization/:organizationId",
    "SuggestionsListPage",
    SuggestionsListPage
  ),
  createPage(
    "/recommendations/organization/:organizationId/:generatedDate",
    "SuggestionsListPage",
    SuggestionsListPage
  ),
  createPage("/recMessages/", "RecMessagesListPage", RecMessagesListPage),
  createPage(
    "/recMessages/organization/:organizationId",
    "RecMessagesListPage",
    RecMessagesListPage
  ),
  createPage("/orgTools/", "OrgToolsPage", OrgToolsPage),
  createPage(
    "/orgTools/organization/:organizationId",
    "OrgToolsPage",
    OrgToolsPage
  ),
  createPage(
    "/management/locations",
    "FacilityRegistrationPage",
    FacilityRegistrationPage
  ),
  createPage(
    "/analysis/collections",
    "AnalysisCollectionsPage",
    AnalysisCollectionsPage
  ),
  createPage(
    "/analysis/organization/:organizationId/collections",
    "AnalysisCollectionsPage",
    AnalysisCollectionsPage
  ),
  createPage("/accounts", "AccountManagementPage", AccountManagementPage),
  createPage(
    "/account/:accountId",
    "AccountManagementPage",
    AccountManagementPage
  ),

  createPage(
    "/admin/locations",
    "FacilityManagementPage",
    FacilityManagementPage
  ),
  createPage(
    "/admin/internalLabels",
    "InternalLabelsManagementPage",
    InternalLabelsManagementPage
  ),

  createPage(
    "/admin/errorLogs",
    "ErrorLogsManagementPage",
    ErrorLogsManagementPage
  ),

  createPage(
    "/admin/customizedReviewsAnalysisPage",
    "CustomizedReviewsAnalysisPage",
    CustomizedReviewsAnalysisPage
  ),
  createPage(
    "/admin/organizations",
    "OrganizationManagementPage",
    OrganizationManagementPage
  ),
  createPage("/admin/internalTools", "InternalToolsPage", InternalToolsPage),
  createPage(
    "/admin/organizations/:organizationId/accounts",
    "AdminAccountManagementPage",
    AdminAccountManagementPage
  ),
  createPage(
    "/admin/deleteGoogleLink",
    "DeleteGoogleLinkPage",
    DeleteGoogleLinkPage
  ),
  createPage(
    "/admin/findUserAccount",
    "FindUserAccountPage",
    FindUserAccountPage
  ),
  createPage("/admin/massSendEmails", "MassSendEmailsPage", MassSendEmailsPage),
  createPage(
    "/instagram_authorize",
    "IntegrateInstagramPage",
    IntegrateInstagramPage
  ),
  createPage(
    "/admin/functionManagement",
    "FunctionManagementPage",
    FunctionManagementPage
  ),
  createPage(
    "/massPost/organization/:organizationId",
    "MassPostPage",
    MassPostPage
  ),
  createPage(
    "/experimental/organizationFeatures/organization/:organizationId",
    "OrganizationFeaturesPage",
    OrganizationFeaturesPage
  ),
  createPage(
    "/instagram_data_deleted/",
    "InstagramDeletedPage",
    InstagramDeletedPage,
    null,
    null,
    true
  ),
  createPage("/error", "ErrorPage", ErrorPage),
  createPage(
    "/location/:locationId/preferences",
    "LocationPreferencesPage",
    LocationPreferencesPage
  ),
  createPage(
    "/activity/locations",
    "LocationActivityPage",
    LocationActivityPage
  ),
  createPage("/demo", "DemoPage", DemoPage),
  createPage("/faq", "FAQ", FaqPage),
  createPage(
    "/admin/locationAlerts",
    "AdminLocationAlertsPage",
    AdminLocationAlertsPage
  ),
  createPage(
    "/data/organization/:organizationId",
    "DataDownloadPage",
    DataDownloadPage
  ),
  createPage("/projects", "ProjectManagementPage", ProjectManagementPage),
  createPage(
    "/projects/organization/:organizationId",
    "ProjectManagementPage",
    ProjectManagementPage
  ),
  createPage("/facility-reports", "FacilityReportsPage", FacilityReportsPage),
  createPage(
    "/facility-reports/:facilityReportId",
    "FacilityReportsPage",
    FacilityReportsPage
  ),
];

export default exportRoute;
